import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ReportTypeCode, type ReportRow, type ReportValidationCheck } from '@/core/models/reports.models';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Select } from '@/kit/Select';

const WEBFILING = 'https://ewf.companieshouse.gov.uk/';

/** The old app's Create Zip File messages (test cases 86478-86480). */
export const SELECT_BOTH_REPORTS = 'Please select both Accounts Report and CIC Report.';
export const PERIODS_DO_NOT_MATCH =
  'Accounts Report and CIC Report accounting periods do not match. Please select reports for the same accounting period.';

const samePeriod = (a: ReportRow, b: ReportRow) => a.fromDate === b.fromDate && a.toDate === b.toDate;

/**
 * Tasks > CIC Submission Wizard (BUG-062): choose the Accounts report and the CIC report separately (86478-86480), create
 * the Companies House zip package (the accounts and the CIC34 report as separate PDFs, and the accounts' iXBRL with the CIC34
 * tags - round 3 #152-#155 - each named for the company number), then
 * file it with Companies House. Both reports must be chosen and cover the same accounting period.
 */
export function CicSubmissionWizard() {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [reportId, setReportId] = useState<string | null>(null);
  const [cicReportId, setCicReportId] = useState<string | null>(null);
  const [packaged, setPackaged] = useState(false);
  const [busy, setBusy] = useState(false);
  /** What stands in the way of the package for the chosen report; null until it has been read. */
  const [checks, setChecks] = useState<ReportValidationCheck[] | null>(null);

  const accountsRow = rows.find((row) => row.id === reportId);
  const cicRow = rows.find((row) => row.id === cicReportId);
  const periodsMatch = !!accountsRow && !!cicRow && samePeriod(accountsRow, cicRow);

  useEffect(() => {
    if (!reportId || !cicReportId || !periodsMatch) return;
    let cancelled = false;
    reportsService.getCicPackageChecks(reportId, cicReportId).then(
      (found) => !cancelled && setChecks(found),
      (error) => !cancelled && notifyError(error),
    );
    return () => {
      cancelled = true;
    };
  }, [reportId, cicReportId, periodsMatch]);

  const blocked = (checks ?? []).some((check) => check.isBlocking);

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    reportsService.getReports(client.id, ReportTypeCode.Annual).then(
      (list) => setRows(list),
      (error) => notifyError(error),
    );
    // ngOnInit: once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createPackage = async () => {
    if (!reportId || !cicReportId) {
      notifyError(SELECT_BOTH_REPORTS);
      return;
    }
    if (!periodsMatch) {
      notifyError(PERIODS_DO_NOT_MATCH);
      return;
    }
    setBusy(true);
    try {
      await reportsService.downloadCicPackage(reportId, cicReportId);
      setPackaged(true);
      notifySuccess('The zip package has been created.');
    } catch (error) {
      notifyBlobError(error, 'The zip package could not be created.');
    } finally {
      setBusy(false);
    }
  };

  const items = rows.map((row) => ({ value: row.id, label: `${row.refNo} (${row.fromDate} - ${row.toDate})` }));
  const reset = () => {
    setPackaged(false);
    setChecks(null);
  };

  return (
    <SideMenuPage menu="task">
      <div className="sidenav-details">
        {/* The side menu's own "CIC Submission Wizard" item already names this page, so the title is kept only as
            the page's accessible name, not shown. A `.sidenav-header` wrapper with nothing visible inside it would
            still draw its border and padding as a stray empty bar (same fix as Accounts Builder's page header), so
            this is a bare hidden heading rather than that wrapper. */}
        <h2 className="cdk-visually-hidden">CIC Submission Wizard</h2>
        <div className="sidenav-body pt-1">
          <div className="form-field">
            <p>Accounts Report</p>
            <Select
              id="cic-accounts-report"
              className="w-387"
              items={items}
              bindLabel="label"
              bindValue="value"
              value={reportId}
              clearable
              placeholder="Select accounts report..."
              onChange={(value) => {
                setReportId((value as string | null) ?? null);
                reset();
              }}
            />
          </div>
          <div className="form-field">
            <p>CIC Report</p>
            <Select
              id="cic-cic-report"
              className="w-387"
              items={items}
              bindLabel="label"
              bindValue="value"
              value={cicReportId}
              clearable
              placeholder="Select CIC report..."
              onChange={(value) => {
                setCicReportId((value as string | null) ?? null);
                reset();
              }}
            />
          </div>
          <p className="font-size-12 text-gray mb-1">
            Companies House needs each document in the zip under its own file name. The package holds the accounts and
            the CIC34 report as separate PDFs and the accounts&apos; iXBRL (tagged with the CIC34 report), each named
            with the company number. Complete the CIC34 report on Report Settings first.
          </p>
          {checks && checks.length > 0 && (
            <div className="mb-1">
              <p className="fw-bold">Before submitting</p>
              {checks.map((check) => (
                <p
                  key={check.code + check.message}
                  className={check.isBlocking ? 'text-danger-100' : 'text-yellow-100'}
                >
                  {check.isBlocking ? 'Blocking: ' : 'Warning: '}
                  {check.message}
                </p>
              ))}
            </div>
          )}
          {checks && checks.length === 0 && <p className="font-size-12 text-gray mb-1">No issues found.</p>}
          <div className="d-flex gap-15">
            <Button
              className="action-button primary-button"
              disabled={busy || blocked}
              onClick={() => void createPackage()}
            >
              Create Zip Package
            </Button>
            <Button
              className="action-button secondary-button"
              disabled={!packaged}
              onClick={() => window.open(WEBFILING, '_blank', 'noopener')}
            >
              File with Companies House
            </Button>
          </div>
          {!packaged && (
            <p className="font-size-12 text-gray mt-5">
              Create the zip package first; it is then filed on Companies House WebFiling.
            </p>
          )}
        </div>
      </div>
    </SideMenuPage>
  );
}
